import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import useSpaceLabels from 'src/hooks/useSpaceLabels'
import {
  Features,
  IConfiguration,
  IConfigurationInput
} from '../../types/graphql'

const MAX_VISIBLE_FEATURES = 6

type LocationFeaturesProps = {
  activeConfig: IConfigurationInput | IConfiguration
}

function LocationFeatures({ activeConfig }: LocationFeaturesProps) {
  const { t } = useTranslation('details')
  const { FEATURES_MAP } = useSpaceLabels()
  const { features } = activeConfig ?? {}
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())

  const shouldShowMore = features && features.length > MAX_VISIBLE_FEATURES

  const visibleFeatures =
    shouldShowMore && !isExpanded
      ? features.slice(0, MAX_VISIBLE_FEATURES)
      : features

  if (!features || features.length === 0) return null

  const toggleCard = (feature: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev)
      if (next.has(feature)) next.delete(feature)
      else next.add(feature)
      return next
    })
  }

  return (
    <section className="mt-8 border-t border-ink-100/15 pt-8 md:mt-12 md:pt-12">
      <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        {t('SECTIONS.FEATURES_EYEBROW')}
      </p>
      <h2 className="mt-3 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
        {t('SECTIONS.WHAT_INCLUDED')}
      </h2>

      <ul className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
        {visibleFeatures?.map((feature) => {
          const featureData = FEATURES_MAP[feature as Features]
          if (!featureData) return null
          const { label, icon } = featureData
          const isCardExpanded = expandedCards.has(feature as string)
          return (
            <li
              key={feature}
              title={label}
              onClick={() => toggleCard(feature as string)}
              className="flex min-w-0 cursor-pointer items-center gap-3 border border-ink-100/25 bg-white-500 px-4 py-3 text-[14px] transition-colors select-none hover:border-blue-500 md:text-[15px]"
            >
              {icon && (
                <i className={`si ${icon} shrink-0 text-base text-blue-500`} />
              )}
              <span
                className={`text-ink-700 ${isCardExpanded ? '[overflow-wrap:anywhere]' : 'truncate'}`}
              >
                {label}
              </span>
            </li>
          )
        })}
      </ul>

      {shouldShowMore && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-5 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500"
        >
          {isExpanded
            ? t('SECTIONS.SHOW_LESS')
            : t('SECTIONS.SHOW_MORE_FEATURES', {
                count: features.length - MAX_VISIBLE_FEATURES
              })}
        </button>
      )}
    </section>
  )
}

export default React.memo(LocationFeatures)
